import React from 'react';
import {
  IonButton,
  IonContent,
  IonIcon,
  IonText,
  IonSpinner,
  IonCard,
  IonCardContent,
  IonPage,
  IonHeader,
  IonItem,
} from '@ionic/react';
import { useTranslation } from 'react-i18next';
import { shieldCheckmarkOutline, informationCircleOutline, lockClosedOutline } from 'ionicons/icons';
import { motion } from 'framer-motion';
import TransactionStatusDisplay from '../TransactionStatus/TransactionStatusDisplay';
import AppHeader from '../AppHeader/AppHeader';
import { useAppSelector, useAppDispatch } from '../../services/redux/hook';
import { setFlowContext } from '../../services/redux/slices/flow-context/flowContext';
import { ebioroApi } from '../../services/api/ebioro/ebioro';
import { WalletRecoveryEmailRequestParameters } from '../../types/types';
import LoginEmailVerify from '../../pages/login/email/LoginEmailVerify';

interface AddSignerModalProps {
  isLoading?: boolean;
  isTracking?: boolean;
  transactionStatus?: string | null;
  transactionMessage?: string | null;
  error?: string | null;
  isCompleted?: boolean;
  isError?: boolean;
  partialToken?: string;
  closeModal?: () => void;
  onConfirm?: () => void;
  onCancel?: () => void;
  onRetry?: () => void;
}

const AddSignerModal: React.FC<AddSignerModalProps> = ({
  isLoading = false,
  isTracking = false,
  transactionStatus = null,
  transactionMessage = null,
  error = null,
  isCompleted = false,
  isError = false,
  closeModal,
  onConfirm = () => console.log('Confirm action'),
  onCancel = () => console.log('Cancel action'),
  onRetry = () => console.log('Retry action')
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const user = useAppSelector((state) => state.user);
  const [isProcessing, setIsProcessing] = React.useState(false);
  const [partialToken, setPartialToken] = React.useState<string | null>(null);

  const handleConfirmSigner = async () => {
    setIsProcessing(true);
    try {
      // Set the flow context for migration
      dispatch(setFlowContext({ 
        flow: 'migrate', 
        redirectAfterPin: '' 
      }));

      // Generate the partial token for wallet recovery
      const params: WalletRecoveryEmailRequestParameters = {
        email: user?.email as string
      };

      const response = await dispatch(ebioroApi.endpoints.recoverWalletByEmail.initiate(params)).unwrap();
      const token = response.partial_token;

      // Set partial token to swap to LoginEmailVerify view
      setPartialToken(token);
      setIsProcessing(false);
    } catch (error) {
      setIsProcessing(false);
      // Still call the original onConfirm if there's an error
      onConfirm();
    }
  };

  // If we have a partial token, render LoginEmailVerify inside the modal
  if (partialToken) {
    return <LoginEmailVerify partialToken={partialToken} closeModal={closeModal} />;
  }

  // Transaction tracking view
  if (isTracking || isCompleted || isError) {
    return (
      <IonPage>
        <IonHeader className="ion-no-border">
          <AppHeader
            title={t('accounts.add-signer.MODAL_TITLE') as string}
          />
        </IonHeader>
        <IonContent className="ion-padding">
          <div className="w-full max-w-md mx-auto h-full flex items-center">
            <TransactionStatusDisplay
              status={transactionStatus as any}
              message={transactionMessage}
              isTracking={isTracking}
              error={error}
              customMessages={{
                pending: t('accounts.add-signer.TRANSACTION_PENDING') as string,
                surgePricing: t('accounts.add-signer.TRANSACTION_SURGE_PRICING') as string,
                processing: t('accounts.add-signer.TRANSACTION_PROCESSING') as string,
                completed: t('accounts.add-signer.TRANSACTION_COMPLETED') as string,
                error: t('accounts.add-signer.TRANSACTION_ERROR') as string,
              }}
              onRetry={onRetry}
              showButtons={isCompleted || isError}
            />
          </div>
        </IonContent>
      </IonPage>
    );
  }

  return (
    <IonPage>
      <IonContent className="ion-padding">
        <div className="flex flex-col min-h-full max-w-md mx-auto">

          {/* Top spacing so content isn't too high */}
          <IonItem lines='none'></IonItem>

          {/* Hero Section with Icon */}
          <div className="flex flex-col items-center text-center gap-4">
            <div className="relative mb-2">
              <div 
                className="w-[90px] h-[90px] rounded-full bg-gradient-to-br from-[#0073C0] to-[#005A99] dark:from-[#EB6034] dark:to-[#CF542E] 
                           flex items-center justify-center"
                style={{ boxShadow: '0 8px 16px rgba(0, 115, 192, 0.2)' }}
              >
                <IonIcon
                  icon={shieldCheckmarkOutline}
                  style={{ fontSize: '40px' }}
                  className="text-white"
                />
              </div>
             
         
            </div>

            <h2 className="text-2xl font-bold text-gray-900 dark:text-white m-0">
              {t('accounts.add-signer.MODAL_TITLE')}
            </h2>

            <p className="text-base text-gray-600 dark:text-gray-300 max-w-xs m-0">
              {t('accounts.add-signer.SUBTITLE')}
            </p>
          </div>

           {/* Spacer to push card downward */}
          <div className="flex-grow" />

          {/* Info card */}
          <div>
            <IonCard className="mt-8 m-0 rounded-2xl shadow-lg border border-gray-100 dark:border-slate-700 bg-white dark:bg-slate-800">
              <IonCardContent className="space-y-4">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-8 h-8 rounded-full bg-blue-50 dark:bg-blue-900/30 flex items-center justify-center">
                    <IonIcon
                      icon={informationCircleOutline}
                      className="text-xl text-blue-600 dark:text-blue-400"
                    />
                  </div>
                  <span className="font-semibold text-base text-gray-900 dark:text-white">
                    {t('accounts.add-signer.WHY_TITLE')}
                  </span>
                </div>

                <div className="space-y-3 mt-2 mb-4">
                  <div className="bg-gradient-to-r from-blue-50 to-blue-50/50 dark:from-blue-900/30 dark:to-blue-900/10 border-l-4 border-blue-500 dark:border-blue-400 rounded-r-xl px-4 py-3">
                    <p className="text-sm font-medium text-gray-800 dark:text-gray-100 m-0">
                      {t('accounts.add-signer.CONTEXT_INTRO')}
                    </p>
                  </div>

                  <p className="text-sm text-gray-600 dark:text-gray-300 m-0 leading-relaxed">
                    {t('accounts.add-signer.EXPLANATION_PART1')}
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-300 m-0 leading-relaxed">
                    {t('accounts.add-signer.EXPLANATION_PART2')}
                  </p>
                </div>
              </IonCardContent>
            </IonCard>
          </div>

          {/* Spacer to push buttons downward */}
          <div className="flex-grow" />

          {/* Buttons */}
          <div className="flex flex-col gap-3 pb-8">
            <IonButton
              expand="block"
              fill="solid"
              color="primary"
              onClick={handleConfirmSigner}
              disabled={isLoading || isProcessing}
              className="h-14 font-semibold text-base rounded-2xl shadow-lg"
            >
              {(isLoading || isProcessing) && <IonSpinner name="crescent" className="mr-2" />}
              {(isLoading || isProcessing)
                ? t('accounts.add-signer.PROCESSING')
                : t('accounts.add-signer.CONFIRM_BUTTON')}
            </IonButton>

            <IonButton
              expand="block"
              fill="outline"
              onClick={onCancel}
              disabled={isLoading || isProcessing}
              className="h-14 font-semibold text-base rounded-2xl"
            >
              {t('accounts.add-signer.CANCEL_BUTTON')}
            </IonButton>

            <div className="text-center pt-3 border-t border-gray-200 dark:border-slate-700 mt-2">
              <small className="text-[12px] text-gray-500 dark:text-gray-400 leading-relaxed">
                {t('accounts.add-signer.SECURITY_NOTICE')}
              </small>
            </div>
          </div>
        </div>
      </IonContent>
    </IonPage>
  );
};

export default AddSignerModal;
